import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Ruler } from "lucide-react";
import { toast } from "sonner";
import {
  type UnitSystem,
  getStoredUnits,
  setStoredUnits,
  resolveUnitSystem,
} from "@/lib/units";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { syncWatchProfile } from "@/lib/straivWatch";
import { Capacitor } from "@capacitor/core";
import { Analytics } from "@/lib/analytics";

interface UnitsSettingsProps {
  /** Controlled value from parent form (Settings page). */
  value?: UnitSystem;
  onChange?: (units: UnitSystem) => void;
}

export function UnitsSettings({ value, onChange }: UnitsSettingsProps) {
  const { t } = useTranslation();
  const { profile, updateProfile } = useProfile();
  const { user } = useAuth();
  const [units, setUnits] = useState<UnitSystem>(
    () => value ?? resolveUnitSystem(profile?.units ?? getStoredUnits()),
  );

  useEffect(() => {
    if (value) setUnits(value);
  }, [value]);

  useEffect(() => {
    if (!value && profile?.units) {
      setUnits(resolveUnitSystem(profile.units));
    }
  }, [profile?.units, value]);

  const apply = async (next: UnitSystem) => {
    const previous = units;
    setUnits(next);
    setStoredUnits(next);
    onChange?.(next);
    if (previous !== next) Analytics.unitsChanged(previous, next);
    // Always persist to profile so Arnold + other screens pick up the change immediately
    // (don't wait for the full Settings form Save).
    try {
      await updateProfile({ units: next } as any);
      toast.success(t("settings.units.saved", { defaultValue: "Units updated" }));
    } catch {
      toast.success(
        t("settings.units.savedLocal", { defaultValue: "Units saved on this device" }),
      );
    }
    if (user?.id && Capacitor.getPlatform() === "ios") {
      void syncWatchProfile(user.id, undefined, undefined, next).catch(() => undefined);
    }
  };

  return (
    <Card className="bg-gradient-card border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-normal">
          <Ruler className="h-5 w-5 text-primary" />
          {t("settings.units.title", { defaultValue: "Units" })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Label className="text-sm text-muted-foreground">
          {t("settings.units.help", {
            defaultValue: "Distances and body weight. Workouts stay accurate either way.",
          })}
        </Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => void apply("metric")}
            className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${
              units === "metric"
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border/50 bg-muted/20 text-muted-foreground"
            }`}
          >
            {t("settings.units.metric", { defaultValue: "Metric" })}
            <span className="mt-1 block text-xs font-normal opacity-80">km · kg</span>
          </button>
          <button
            type="button"
            onClick={() => void apply("imperial")}
            className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${
              units === "imperial"
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border/50 bg-muted/20 text-muted-foreground"
            }`}
          >
            {t("settings.units.imperial", { defaultValue: "Imperial" })}
            <span className="mt-1 block text-xs font-normal opacity-80">mi · lb</span>
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
